import { AbstractControl, Validators } from '@angular/forms';
import { CompanyControllingPersonModel, CompanyModel, ImporterOfRecordContactInformationModel } from '@data-layer/models/companies';
import { AddressModel, CountryModel } from '@data-layer/models/common';
import { UserModel } from '@data-layer/models/user';
import { enumIdentifierQualifier } from '@data-layer/enums/common';
import {
  isPartnership,
  isStateOfIncorporationApplicable,
  minControllingPersons,
  requiresControllingPersons,
  requiresPhotoId,
} from '@constants/business-types';
import { requiresSs4Document } from '@constants/company-documents';
import { IorContactMatchHelper } from './ior-contact-match.helper';

export interface CompanyPoaReadinessInput {
  company: CompanyModel;
  /** Company documents contain an enumDocumentType.ID_DOCUMENT entry. */
  hasPhotoIdDocument: boolean;
  /** #13269 — person ids that have an UPLOADED ControllingPersonStateId (112) document. */
  controllingPersonScanPersonIds: number[];
  /** #13356 — company documents contain an enumDocumentType.SS4 entry. */
  hasSs4Document: boolean;
  /** Current user (CompaniesService.getUser()) — needed for the IOR-match rule. */
  user: UserModel | null;
  /** #13175/#13212 — POA already signed/paid → the IOR-match rule is skipped. */
  isPoaGrandfathered: boolean;
}

/**
 * #13260 — model-based mirror of EditCompanyComponent.isPoaFieldsComplete for hosts that
 * render company-power-attorney WITHOUT the company form (bond purchase, ES POA sidebar).
 * Checks required-ness only: the company on those pages is already persisted, so length
 * constraints (line1/postalCode/payerUnitNumber) enforced by the form are not re-checked.
 *
 * Every rule here MUST have a counterpart in the company-page form (fullCompanyFormFields +
 * EditCompanyComponent.isPoaFieldsComplete): a rule the form does not enforce leaves the button enabled
 * on the company page and disabled in the sidebar for the same company (#13497 — ACH agreement flag).
 */
export class PoaCompletenessHelper {
  public static isCompanyPoaReady(input: CompanyPoaReadinessInput): boolean {
    const company: CompanyModel = input.company;
    if (!company) {
      return false;
    }

    if (!PoaCompletenessHelper.hasText(company.businessName)) {
      return false;
    }

    if (!PoaCompletenessHelper.isAddressComplete(company.address)) {
      return false;
    }

    // businessType/identifier are required for US OR customs-enabled companies (edit-company
    // applyInitialValidators / isUsaSubscription / isAllowCustomsSubscription). #13497 — the ACH agreement
    // flag is NOT part of the gate: the company form never validates that checkbox (#12900), so requiring
    // it here kept the sidebar/CB hosts disabled for companies the company page already lets sign
    if (company.isUnitedStates || company.isAllowedForCustoms) {
      if (!PoaCompletenessHelper.hasText(company.identifier) || !company.businessType) {
        return false;
      }
    }

    if (company.isUnitedStates) {
      // #13188 — US Corporation/LLC/Partnership must have a State of Incorporation (the ticket's repro).
      // #13520 — only with a US address: the company form hides the field for a foreign ANI importer
      const country: CountryModel | null = company.address ? company.address.country : null;
      if (isStateOfIncorporationApplicable(company.isUnitedStates, country, company.businessType) && !company.stateOfIncorporation) {
        return false;
      }
      if (company.identifierQualifier !== enumIdentifierQualifier.ANI && !company.isAllowedRegistration) {
        return false;
      }
    }

    // #13188 — Individual and Sole Proprietor business types require a Photo ID document
    if (requiresPhotoId(company.businessType) && !input.hasPhotoIdDocument) {
      return false;
    }

    // #13356 — EIN-registered companies must have an SS-4, unless the POA is already signed/paid.
    // isUnitedStates mirrors the block's visibility rule (the uploader only renders for US companies).
    if (company.isUnitedStates && requiresSs4Document(company.identifierQualifier) && !input.isPoaGrandfathered && !input.hasSs4Document) {
      return false;
    }

    // #13269 — Corporation/LLC/Partnership must have enough complete controlling persons
    // (mirrors the backend completeness rule that gates POST digital-signs)
    if (!PoaCompletenessHelper.isControllingPersonsReady(company, input.controllingPersonScanPersonIds || [])) {
      return false;
    }

    return PoaCompletenessHelper.isIorContactReady(company, input.user, input.isPoaGrandfathered);
  }

  private static isControllingPersonsReady(company: CompanyModel, scanPersonIds: number[]): boolean {
    if (!requiresControllingPersons(company.businessType)) {
      return true;
    }
    if (isPartnership(company.businessType) && !company.partnershipType) {
      return false;
    }
    const completePersons: CompanyControllingPersonModel[] = (company.controllingPersons || []).filter(
      (person: CompanyControllingPersonModel) =>
        PoaCompletenessHelper.hasText(person.firstName) &&
        PoaCompletenessHelper.hasText(person.lastName) &&
        PoaCompletenessHelper.hasText(person.socialSecurityNumber) &&
        person.isUsCitizenOrResident === true &&
        scanPersonIds.includes(person.id),
    );
    return completePersons.length >= minControllingPersons(company.businessType);
  }

  private static isIorContactReady(company: CompanyModel, user: UserModel | null, isPoaGrandfathered: boolean): boolean {
    const ior: ImporterOfRecordContactInformationModel = company.importerOfRecordContactInformation;
    if (
      !ior ||
      !PoaCompletenessHelper.hasText(ior.firstName) ||
      !PoaCompletenessHelper.hasText(ior.lastName) ||
      !PoaCompletenessHelper.hasText(ior.phoneNumber) ||
      !PoaCompletenessHelper.hasText(ior.emailAddress)
    ) {
      return false;
    }

    // Exact parity with the form's Validators.email (it only reads control.value)
    if (Validators.email({ value: ior.emailAddress } as AbstractControl)) {
      return false;
    }

    // #13212 — the IOR contact must not match the current user, unless "I am the IOR" or grandfathered
    if (!company.isImporterOfRecord && !isPoaGrandfathered) {
      if (IorContactMatchHelper.emailMatchesUser(ior, user) || IorContactMatchHelper.phoneMatchesUser(ior, user)) {
        return false;
      }
    }

    return true;
  }

  // state is required for every country here: the company page builds its form from fullCompanyFormFields
  // (address.state → Validators.required), unlike the sidebar key sets that use CustomValidator.stateIsRequired
  private static isAddressComplete(address: AddressModel): boolean {
    if (!address || !address.state || !address.country) {
      return false;
    }
    return (
      PoaCompletenessHelper.hasText(address.line1) &&
      PoaCompletenessHelper.hasText(address.city) &&
      PoaCompletenessHelper.hasText(address.postalCode)
    );
  }

  private static hasText(value: string | null | undefined): boolean {
    return !!value && value.toString().trim() !== '';
  }
}
